import { ComponentConfig, FieldLabel } from '@measured/puck';
import React, { useEffect, useState } from 'react';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
  PaddingType,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { MarginProps, marginFields } from '../../puck/reusable-props/NewMargin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import { AxiosInstance } from 'axios';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  getSpanPercentage,
} from '../../utils/helper';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import WithAccordion from '../customComponents/CustomAccordion';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ImageProps, imageProps } from '../../puck/reusable-props/image-props';
import PreviewProduct from './PreviewProduct';
import InputField from '../../../components/common/input-field';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { setFunnelishProducts } from '../../redux/slices/cartSlice';
import { toolTipInfo } from '../../utils/constants';
import PaddingComponent from '../customComponents/PaddingComponent';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import WidthComponent, { IWidthComponentProps } from '../blocks/widthComponent';
import ProductModal, {
  defaultProduct,
  IProductModalProps,
} from './AddProductModal';

export type PredefinedPropsForUpsell = MarginProps &
  ImageProps &
  PaddingProps & {
    background: {
      color: string;
      isInherit: boolean;
    };
    products: IProductModalProps[];
    productTitle: CustomizeProps;
    width: string;
    tagWidth: IWidthComponentProps;
    tagPosition: string;
    border: IBorderValueProps;
    borderForProducts: IBorderValueProps;
    radioColor: string;
    radioSize: string;
    leftHeader: string;
    rightHeader: string;
    tagStyles: {
      isBlink: string;
      borderTopLeft: string;
      borderTopRight: string;
      borderBottomLeft: string;
      borderBottomRight: string;
      padding: PaddingType;
      backgroundColor: string;
    };
  };

export interface PredefinedProductComponentProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const PredefinedUpsell = ({
  useAppSelector,
  baseService,
  useAppDispatch,
}: PredefinedProductComponentProps &
  ReduxDispatchProps): ComponentConfig<PredefinedPropsForUpsell> => {
  return {
    label: (
      <CustomTooltip title="PreDefined">
        <span>Predefined Upsell</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      products: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <ProductModal
            value={value}
            onChange={onChange}
            baseService={baseService}
          />
        ),
      },
      leftHeader: {
        ...QuillTextEditor('Left Header', true, 1).content,
      },
      rightHeader: {
        ...QuillTextEditor('Right Header', true, 2).content,
      },
      productTitle: {
        type: 'custom',
        label: 'Product Title',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title={field?.label}
            isText={false}
          />
        ),
      },
      ...imageProps([]),
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      borderForProducts: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={onChange}
            title="Border For Products"
          />
        ),
      },
      radioColor: {
        type: 'custom',
        label: 'Selected radio color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      radioSize: {
        type: 'custom',
        label: 'Radio Size',
        render: ({ value, onChange }) => (
          <>
            <label>Radio Size</label>
            <input
              type="Number"
              value={value || ''}
              onChange={newValue => {
                onChange(newValue.target.value);
              }}
            />
          </>
        ),
      },
      tagPosition: {
        type: 'select',
        label: 'Tag Position',
        options: [
          { label: 'Image Top', value: 'top' },
          { label: 'Image Right', value: 'right' },
        ],
      },
      width: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <InputField
            value={value}
            label="Width"
            onChange={e => {
              const newValue = getSpanPercentage(e);
              if (newValue !== null) {
                onChange(newValue);
              }
            }}
          />
        ),
      },
      tagWidth: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthComponent onChange={onChange} value={value} />
        ),
      },
      tagStyles: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Tag Customization">
            <ColorPicker
              value={value?.backgroundColor || '#000000'}
              onChange={color =>
                onChange({ ...(value || {}), backgroundColor: color })
              }
              label="Background Color"
              tooltip="Background Color"
            />
            <FieldLabel
              label={
                (
                  <CustomTooltip title="Border Radius">
                    <span>Border Radius</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }>
              <div className="grid grid-cols-[1fr_1fr] gap-2 border-t-1">
                <InputField
                  label="Top-Left"
                  value={value?.borderTopLeft}
                  className="h-[42px]"
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopLeft: newValue,
                    });
                  }}
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderTopRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderTopRight: newValue,
                    });
                  }}
                  label="Top-Right"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomLeft}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomLeft: newValue,
                    });
                  }}
                  label="Bottom-Left"
                />
                <InputField
                  className="h-[42px]"
                  value={value?.borderBottomRight}
                  onChange={e => {
                    const newValue = e.replace(/[^0-9%]/g, '');
                    onChange({
                      ...value,
                      borderBottomRight: newValue,
                    });
                  }}
                  label="Bottom-Right"
                />
              </div>
            </FieldLabel>
            <FieldLabel
              label={
                (
                  <CustomTooltip
                    title={'Pulse_Animation' as keyof typeof toolTipInfo}>
                    <span>Pulse Animation</span>
                  </CustomTooltip>
                ) as React.ReactNode as string
              }
              el="div"
              className="w-full">
              <div className="w-full p-2">
                <ToggleButtonGroup
                  value={value?.isBlink}
                  exclusive
                  className="w-full"
                  onChange={(_e, selectValue) =>
                    onChange({ ...value, isBlink: selectValue })
                  }>
                  {[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ].map((option, index: number) => (
                    <ToggleButton
                      key={index}
                      size="small"
                      value={option.value}
                      disableRipple
                      aria-label={option.value}
                      className="flex-1">
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
            </FieldLabel>
            <PaddingComponent
              value={value?.padding}
              onChange={newPadding => {
                onChange({
                  ...value,
                  padding: {
                    ...value.padding,
                    ...newPadding,
                  },
                });
              }}
              title="Padding"
            />
          </WithAccordion>
        ),
      },
      ...backgroungFields,
      ...paddingFields,
      ...marginFields,
    },
    defaultProps: {
      borderForProducts: {
        deviceType: 'desktop',
        borderBottomLeft: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderBottomRight: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopLeft: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopRight: {
          desktop: '10',
          tablet: '10',
          mobile: '10',
        },
        borderTopWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderRightWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderBottomWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderLeftWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderWidth: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
        borderStyle: {
          desktop: 'border-solid',
          tablet: 'border-solid',
          mobile: 'border-solid',
        },
        borderColor: {
          desktop: '#a3a3a3',
          tablet: '#a3a3a3',
          mobile: '#a3a3a3',
        },
      },
      radioColor: '#2563EB',
      radioSize: '20',
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '4',
          mobile: '2',
          tablet: '4',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '4',
          mobile: '2',
          tablet: '4',
        },
      },
      tagWidth: {
        deviceType: 'desktop',
        width: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
      },
      tagPosition: 'top',
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingLeft: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingRight: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
        paddingTop: {
          desktop: '8',
          mobile: '6',
          tablet: '8',
        },
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      ...borderProps,
      leftHeader: '<p>Product</p>',
      rightHeader: '<p>Price</p>',
      image: {
        height: {
          desktop: '60',
          mobile: '60',
          tablet: '60',
        },
        maxWidth: {
          desktop: '60',
          mobile: '60',
          tablet: '60',
        },
        deviceType: 'desktop',
      },
      products: [defaultProduct],
      width: '100%',
      productTitle: {
        weight: {
          mobile: 'font-semibold',
          tablet: 'font-semibold',
          desktop: 'font-semibold',
        },
        deviceType: 'desktop',
        text: '',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        hoverColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      tagStyles: {
        isBlink: 'no',
        borderTopLeft: '0',
        borderTopRight: '0',
        borderBottomLeft: '0',
        borderBottomRight: '0',
        ...paddingProps,
        backgroundColor: '#FFFFFF',
      },
    },
    render: ({
      margin,
      padding,
      editMode,
      background,
      leftHeader,
      rightHeader,
      border,
      image,
      products,
      width,
      productTitle,
      tagStyles,
      tagWidth,
      borderForProducts,
      radioColor,
      radioSize,
      tagPosition,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const dispatch = useAppDispatch();
      const { cartData } = useAppSelector(state => state.cart);
      const { storeData } = useAppSelector(state => state.store);
      const { pageId } = useAppSelector(state => state.checkout);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [selectedProduct, setSelectedProduct] = useState<string | null>(
        null,
      );
      let maxContainerWidth: string;
      if (tagWidth?.width?.[currentDeviceType]?.includes('%')) {
        maxContainerWidth = tagWidth?.width?.[currentDeviceType];
      } else if (
        tagWidth?.width?.[currentDeviceType] &&
        tagWidth?.width?.[currentDeviceType] !== 'none'
      ) {
        maxContainerWidth = `${tagWidth?.width?.[currentDeviceType]}px`;
      } else {
        maxContainerWidth = '100%';
      }

      useEffect(() => {
        setSelectedProduct(
          products?.find(item => item.defaultProduct)?.productDetails.id ||
            null,
        );
        dispatch(setFunnelishProducts(products || []));
      }, [products]);

      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...getBorderProps(border, currentDeviceType),
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
            width: isNaN(+width) ? width : width + 'px',
          }}>
          <div className="flex justify-between text-sm font-medium mb-3">
            <div dangerouslySetInnerHTML={{ __html: leftHeader }} />
            <div dangerouslySetInnerHTML={{ __html: rightHeader }} />
          </div>

          <hr className="border-t-1 border-gray-200 mb-2" />
          <div className="grid grid-cols-1 gap-3">
            <PreviewProduct
              pageId={pageId}
              productTitle={productTitle}
              maxContainerWidth={maxContainerWidth}
              data={products}
              cartData={cartData}
              tagPosition={tagPosition}
              useAppSelector={useAppSelector}
              storeData={storeData}
              dispatch={dispatch}
              currentDeviceType={currentDeviceType}
              image={image}
              tagStyles={tagStyles}
              selectedProduct={selectedProduct || ''}
              setSelectedProduct={setSelectedProduct}
              borderForProducts={borderForProducts}
              radioColor={radioColor}
              radioSize={radioSize}
            />
          </div>
        </div>
      );
    },
  };
};
